import { Injectable, inject, signal } from '@angular/core';
import { LanguageService } from '../i18n/language.service';

export type NotificationKind = 'success' | 'error' | 'info' | 'warning';

export interface AppNotification {
  id: number;
  kind: NotificationKind;
  message: string;
}

let nextId = 1;

const AUTO_DISMISS_MS = 5_000;
const DUPLICATE_WINDOW_MS = 1_500;
const MAX_VISIBLE_NOTIFICATIONS = 3;
const SILENT_BACKGROUND_ERROR = /^(impossible de (charger|récupérer|vérifier)|erreur lors du chargement)/i;

/**
 * File de notifications légère, sans dépendance externe.
 *
 * Les mêmes erreurs peuvent être remontées à la fois par un intercepteur et
 * par plusieurs appels parallèles d'une page. La file déduplique donc les
 * messages très rapprochés et limite le nombre de toasts visibles afin de ne
 * jamais recouvrir l'interface.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly language = inject(LanguageService);
  private readonly notificationsSignal = signal<AppNotification[]>([]);
  private readonly recentlyDisplayed = new Map<string, number>();

  readonly notifications = this.notificationsSignal.asReadonly();

  success(message: string): void {
    this.push('success', message);
  }

  error(message: string): void {
    // Une liste vide ou une ressource non autorisée doit se traduire par un
    // état vide dans la page, jamais par une pile de toasts génériques.
    if (SILENT_BACKGROUND_ERROR.test(message.trim())) {
      return;
    }

    this.push('error', message);
  }

  info(message: string): void {
    this.push('info', message);
  }

  warning(message: string): void {
    this.push('warning', message);
  }

  dismiss(id: number): void {
    this.notificationsSignal.update((list) => list.filter((notification) => notification.id !== id));
  }

  clear(): void {
    this.notificationsSignal.set([]);
  }

  private push(kind: NotificationKind, rawMessage: string): void {
    const message = this.language.translate(rawMessage).trim();
    if (message === '') {
      return;
    }

    const now = Date.now();
    const duplicateKey = `${kind}:${message.toLocaleLowerCase('fr')}`;
    const lastDisplayedAt = this.recentlyDisplayed.get(duplicateKey) ?? 0;

    if (now - lastDisplayedAt < DUPLICATE_WINDOW_MS) {
      return;
    }

    this.recentlyDisplayed.set(duplicateKey, now);
    this.removeExpiredDuplicateKeys(now);

    const notification: AppNotification = { id: nextId++, kind, message };
    this.notificationsSignal.update((list) =>
      [...list, notification].slice(-MAX_VISIBLE_NOTIFICATIONS),
    );

    window.setTimeout(() => this.dismiss(notification.id), AUTO_DISMISS_MS);
  }

  private removeExpiredDuplicateKeys(now: number): void {
    for (const [key, displayedAt] of this.recentlyDisplayed.entries()) {
      if (now - displayedAt >= DUPLICATE_WINDOW_MS) {
        this.recentlyDisplayed.delete(key);
      }
    }
  }
}
